import { makeAutoObservable } from 'mobx';
import DocumentFolder from 'main/stores/document_folders/DocumentFolder';
import {successAlert} from 'shared/tools';
import {t, api} from 'shared/core';
import {endpoints, types} from 'main/tools';
import {DomainStore, fetchModels} from 'shared/store';
import _ from 'lodash';

class DocumentFoldersState {
  store = new DomainStore();
  agent;

  editingFolder = {};
  errors = {};
  editDocumentFolderModalOpen = false;
  editMessage = 'company_settings.document_folders.Edit Document Folder';
  folders = [];
  reorderingFolders = [];
  reorderParentFolder = null;
  interactiveContextState = {};

  constructor() {
    makeAutoObservable(this, {
      store: false,
      agent: false
    });
  }

  setInteractiveAgent(agent) {
    this.agent = agent;
  }

  updateInteractiveContextState(interactiveContextState) {
    this.interactiveContextState = interactiveContextState;
  }

  get canViewNestedFolders() {
    const search = this.interactiveContextState.filter?.get('search') || '';

    return _.isEmpty(search);
  }

  async load() {
    await this.store._compose([
      endpoints.FOLDERS.ALL
    ]);

    this.folders = this.store._getAll(types.FOLDER, DocumentFolder);
  }

  async fetchSubfolders(folder) {
    if (folder.subfolders && folder.subfolders.length > 0) return;

    const store = new DomainStore();
    await store._compose([endpoints.FOLDERS.withParent(folder.id)]);
    folder.subfolders = store._getAll(types.FOLDER, DocumentFolder);
  }

  async deleteDocumentFolder(folder) {
    await this.store.destroy(folder);
    successAlert(t('company_settings.document_folders.alerts.Folder deleted successfully.'));
    this.agent.refresh();
  }

  addDocumentFolder() {
    this.editMessage = 'company_settings.document_folders.Add Document Folder';
    this.editDocumentFolderModalOpen = true;
    this.editingFolder = new DocumentFolder();
  }

  addSubfolder(parentFolder) {
    this.editMessage = 'company_settings.document_folders.Add Document Folder';
    this.editDocumentFolderModalOpen = true;
    this.editingFolder = new DocumentFolder({parentId: parentFolder.id});
  }

  editDocumentFolder(folder) {
    this.editMessage = 'company_settings.document_folders.Edit Document Folder';
    this.editDocumentFolderModalOpen = true;
    this.editingFolder = new DocumentFolder(folder);
  }

  closeEditDocumentFolderModal() {
    this.editDocumentFolderModalOpen = false;
  }

  async openReorderFoldersModal(parentFolder = null) {
    this.reorderParentFolder = parentFolder;
    await this.fetchReorderingSubfolders(parentFolder);
  }

  async selectReorderParentFolder(parentFolder) {
    this.reorderParentFolder = parentFolder;
    await this.fetchReorderingSubfolders(parentFolder);
  }

  async fetchReorderingSubfolders(parentFolder) {
    this.reorderingFolders = _.map(await fetchModels(
      endpoints.FOLDERS.withParent(parentFolder && parentFolder.id),
      types.FOLDER
    ), folder => new DocumentFolder(folder));
  }

  async saveFolderOrders() {
    const folderParams = {};
    this.reorderingFolders.forEach(folder => folderParams[folder['id']] = folder['order']);

    await api.patch(endpoints.FOLDERS.REORDER, {'folders': folderParams});

    this.closeReorderFoldersModal();
    this.agent.refresh();
  }

  async saveDocumentFolder() {
    const {model, errors} = this.editingFolder.isNew ?
      await this.store.post(endpoints.FOLDERS.ALL, types.FOLDER, this.editingFolder) :
      await this.store.patch(this.editingFolder);

    this.errors = errors;

    if (model) {
      this.agent.refresh();
      await this.load();
      this.closeEditDocumentFolderModal();
      successAlert(t('company_settings.document_folders.alerts.Folder information saved.'));
    }
  }

  customLinksFor(folder) {
    const links = [
      {
        order: 0,
        text: 'company_settings.document_folders.Add Subfolder',
        action: folder => this.addSubfolder(folder)
      }
    ];

    if (folder.hasSubfolders) {
      links.push({
        order: 1,
        text: 'company_settings.document_folders.Reorder Subfolders',
        action: folder => this.openReorderFoldersModal(folder)
      });
    }

    return links;
  }

  get parentFolderOptions() {
    const editingId = this.editingFolder.id;
    if (!editingId) return this.folders;

    return this.folders.filter(folder => folder.id !== editingId);
  }

  get sortedFolders() {
    return _.orderBy(this.folders, 'order');
  }
}

export default DocumentFoldersState;
